// # Products page component
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MdDiscount } from 'react-icons/md';  // Add discount icon import
import { MdEmail } from 'react-icons/md';
import { FaWhatsapp, FaXTwitter, FaFacebook, FaInstagram } from 'react-icons/fa6';
import DashboardHeader from '../../components/DashboardHeader';
import Header from '../components/header';  // # Import regular header
import DashboardFooter from '../../components/DashboardFooter';
import { auth, db } from '../../auth/firebase';
import { doc, updateDoc } from 'firebase/firestore';  // # Import for updating theme
import cacheManager from '../../auth/cache_manager';  // # Import cache manager
import { useAuthStore } from '../../auth/state';  // # Import auth store
import './products.css';
import './products_compare.css';  // Import comparison table styles

function Products() {
  const navigate = useNavigate();
  const contactRef = useRef(null);
  const [showEgyptianPrices, setShowEgyptianPrices] = useState(false);
  // # Removed subscription form state variables
  const location = useLocation();
  const [isDarkMode, setIsDarkMode] = useState(false);  // # Add dark mode state
  const { isAuthenticated } = useAuthStore();  // # Get auth state

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (auth.currentUser && auth.currentUser.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${auth.currentUser.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, []);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (auth.currentUser && auth.currentUser.uid) {
        // # Update the user document
        await updateDoc(doc(db, 'users', auth.currentUser.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${auth.currentUser.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${auth.currentUser.uid}`, updatedUserData);
        }
      }
      
      // # Dispatch theme change event for footer and other components
      const themeChangeEvent = new CustomEvent('theme_change', {
        detail: { isDark: newTheme }
      });
      window.dispatchEvent(themeChangeEvent);
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  const internationalSubscriptions = [
    {
      title: 'USMLE Step 1 Library',
      type: 'library',
      description: 'Access to comprehensive Step 1 study materials',
      durations: [
        { 
          months: 1, 
          price: '$33',
          features: ['1 month access', 'No exam extension']
        },
        { 
          months: 3, 
          price: '$45',
          features: ['3 months access', 'No exam extension']
        },
        { 
          months: 6, 
          price: '$60',
          originalPrice: '$75',
          features: ['6 months access', 'Up to 2 months free with exam scheduling']
        },
        { 
          months: 12, 
          price: '$90',
          originalPrice: '$110',
          features: ['12 months access', 'Up to 4 months free with exam scheduling']
        },
        { 
          months: 24, 
          price: '$120',
          features: ['24 months access', 'Up to 6 months free with exam scheduling']
        },
        { 
          months: -1, // Using -1 to represent lifetime
          price: '$160',
          features: ['Lifetime access', 'No exam extension needed']
        }
      ]
    },
    {
      title: 'USMLE Step 2 Library',
      type: 'library',
      description: 'Access to comprehensive Step 2 study materials',
      durations: [
        { 
          months: 1, 
          price: '$33',
          features: ['1 month access', 'No exam extension']
        },
        { 
          months: 3, 
          price: '$45',
          features: ['3 months access', 'No exam extension']
        },
        { 
          months: 6, 
          price: '$60',
          originalPrice: '$75',
          features: ['6 months access', 'Up to 2 months free with exam scheduling']
        },
        { 
          months: 12, 
          price: '$90',
          originalPrice: '$110',
          features: ['12 months access', 'Up to 4 months free with exam scheduling']
        },
        { 
          months: 24, 
          price: '$120',
          features: ['24 months access', 'Up to 6 months free with exam scheduling']
        },
        { 
          months: -1, // Using -1 to represent lifetime
          price: '$160',
          features: ['Lifetime access', 'No exam extension needed']
        }
      ]
    },
    {
      title: 'Lifetime Bundle (Both Libraries)',
      type: 'bundle',
      description: 'Lifetime access to both Step 1 & Step 2 libraries',
      price: '$200',
      features: [
        'Lifetime access to Step 1 library',
        'Lifetime access to Step 2 library',
        'No exam extension needed',
        'One-time payment',
        'Best value for long-term preparation'
      ]
    },
    {
      title: 'USMLE Step 1 Mentorship',
      type: 'mentorship',
      description: 'Complete Step 1 mentorship program including library access and tutoring',
      isService: true,
      price: '$70 Enrollment fees + $35/month',
      originalPrice: '$80 Enrollment fees',
      features: [
        'Full Step 1 library access',
        'Complete mentorship and tutoring',
        'Personalized study plans',
        'Regular progress tracking'
      ]
    },
    {
      title: 'USMLE Step 2 Mentorship',
      type: 'mentorship',
      description: 'Complete Step 2 mentorship program including library access and tutoring',
      isService: true,
      price: '$70 Enrollment fees + $35/month',
      originalPrice: '$80 Enrollment fees',
      features: [
        'Full Step 2 library access',
        'Complete mentorship and tutoring',
        'Personalized study plans',
        'Regular progress tracking'
      ]
    }
  ];

  const egyptianSubscriptions = [
    {
      title: 'USMLE Step 1 Library',
      type: 'library',
      description: 'Access to comprehensive Step 1 study materials',
      durations: [
        { 
          months: 1, 
          price: '1250 LE',
          features: ['1 month access', 'No exam extension']
        },
        { 
          months: 3, 
          price: '1500 LE',
          features: ['3 months access', 'No exam extension']
        },
        { 
          months: 6, 
          price: '2000 LE',
          features: ['6 months access', 'Up to 2 months free with exam scheduling']
        },
        { 
          months: 12, 
          price: '3000 LE',
          features: ['12 months access', 'Up to 4 months free with exam scheduling']
        },
        { 
          months: 24, 
          price: '4500 LE',
          features: ['24 months access', 'Up to 6 months free with exam scheduling']
        },
        { 
          months: -1, // Using -1 to represent lifetime
          price: '5000 LE',
          features: ['Lifetime access', 'No exam extension needed']
        }
      ]
    },
    {
      title: 'USMLE Step 2 Library',
      type: 'library',
      description: 'Access to comprehensive Step 2 study materials',
      durations: [
        { 
          months: 1, 
          price: '1250 LE',
          features: ['1 month access', 'No exam extension']
        },
        { 
          months: 3, 
          price: '1500 LE',
          features: ['3 months access', 'No exam extension']
        },
        { 
          months: 6, 
          price: '2000 LE',
          features: ['6 months access', 'Up to 2 months free with exam scheduling']
        },
        { 
          months: 12, 
          price: '3000 LE',
          features: ['12 months access', 'Up to 4 months free with exam scheduling']
        },
        { 
          months: 24, 
          price: '4500 LE',
          features: ['24 months access', 'Up to 6 months free with exam scheduling']
        },
        { 
          months: -1, // Using -1 to represent lifetime
          price: '5000 LE',
          features: ['Lifetime access', 'No exam extension needed']
        }
      ]
    },
    {
      title: 'Lifetime Bundle (Both Libraries)',
      type: 'bundle',
      description: 'Lifetime access to both Step 1 & Step 2 libraries',
      price: '7000 LE',
      features: [
        'Lifetime access to Step 1 library',
        'Lifetime access to Step 2 library',
        'No exam extension needed',
        'One-time payment',
        'Best value for long-term preparation'
      ]
    },
    {
      title: 'USMLE Step 1 Mentorship',
      type: 'mentorship',
      description: 'Complete Step 1 mentorship program including library access and tutoring',
      isService: true,
      price: '2200 LE Enrollment fees + 1000 LE/month',
      features: [
        'Full Step 1 library access',
        'Complete mentorship and tutoring',
        'Free 3-month break between steps'
      ]
    },
    {
      title: 'USMLE Step 2 Mentorship',
      type: 'mentorship',
      description: 'Complete Step 2 mentorship program including library access and tutoring',
      isService: true,
      price: '2200 LE Enrollment fees + 1000 LE/month',
      features: [
        'Full Step 2 library access',
        'Complete mentorship and tutoring',
        'Free 3-month break between steps'
      ]
    }
  ];

  const subscriptionTypes = showEgyptianPrices ? egyptianSubscriptions : internationalSubscriptions;

  const heroStyle = {
    background: `linear-gradient(rgba(44, 62, 80, 0.7), rgba(44, 62, 80, 0.7)), url(${process.env.PUBLIC_URL + '/icons/productsx.jpg'})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat'
  };

  // # Add scroll handler
  const scrollToContact = () => {
    contactRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // # Generate WhatsApp message based on product
  const generateWhatsAppMessage = (product) => {
    if (!product) {
      return `Hello! I'm interested in your USMLE preparation programs. Could you please provide me with more information about your available plans and pricing?`;
    }

    let productName = '';
    
    // Determine product name based on type
    if (product.months === -2 || product.type === 'bundle') {
      // Lifetime Bundle
      productName = `Lifetime Bundle (Both Libraries)`;
    } else if (product.isService) {
      // Mentorship programs
      const step = product.title.includes('Step 1') ? 'Step 1' : 'Step 2';
      productName = `USMLE ${step} Mentorship`;
    } else if (product.months) {
      // Library subscriptions with duration
      const step = product.title?.includes('Step 1') ? 'Step 1' : 'Step 2';
      const duration = product.months === -1 ? 'Lifetime' : `${product.months} month${product.months > 1 ? 's' : ''}`;
      productName = `USMLE ${step} Library (${duration})`;
    } else {
      // Fallback
      productName = `USMLE preparation program`;
    }
    
    const message = `Hello, I am interested in ${productName} subscription. Could you please help me get started?`;
    
    return message;
  };

  // # Handle subscription button click - redirect to WhatsApp
  const handleSubscribe = (product = null) => {
    const message = generateWhatsAppMessage(product);
    const encodedMessage = encodeURIComponent(message);
    const whatsappURL = `https://wa.me/201225040602?text=${encodedMessage}`;
    
    // Open WhatsApp in a new tab
    window.open(whatsappURL, '_blank');
  };

  const renderDuration = (months) => {
    if (months === -2) return 'Lifetime (Both Libraries)';
    if (months === -1) return 'Lifetime';
    if (months === 1) return '1 Month';
    return `${months} Months`;
  };

  // # Update the subscription card render to include both buttons, but only for library subscriptions
  const renderSubscriptionButtons = (isLibrary = true, product = null) => (
    <div className="prdct_subscription-buttons">
      <button 
        className="prdct_subscribe-btn" 
        onClick={() => handleSubscribe(product)}
      >
        {isLibrary ? 'Subscribe' : 'Get Started'}
      </button>
      {isLibrary && product?.months !== -2 && (
        <button 
          className="prdct_samples-btn" 
          onClick={() => navigate('/samples')}
        >
          View Samples
        </button>
      )}
    </div>
  );

  return (
    <div className={`prdct_products-page ${isDarkMode ? 'prdct_dark-mode' : ''}`}>
      {isAuthenticated ? (
        <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
      ) : (
        <Header />
      )}
      
      <main className="prdct_products-content">
        <section className="prdct_hero-section" style={heroStyle}>
          <div className="prdct_hero-overlay"></div>
          <h1>Our Products</h1>
          <p>Comprehensive USMLE preparation resources tailored to your success</p>
          <div className="prdct_hero-decoration">
            <div className="prdct_hero-shape"></div>
          </div>
        </section>

        <div className="prdct_pricing-notice prdct_group-notice">
          <div className="prdct_pricing-notice-icon-wrapper">
            <MdDiscount className="prdct_pricing-notice-icon" />
          </div>
          <p>
            We offer <strong>additional discounts</strong> for group enrollments: <strong>25% off for groups of 3</strong> and <strong>50% off for groups of 6</strong> students enrolling together. Contact us to learn more about our <strong>group enrollment benefits</strong>.
          </p>
        </div>

        <div className="prdct_pricing-toggle">
          <button 
            className={`prdct_toggle-btn ${!showEgyptianPrices ? 'prdct_active' : ''}`}
            onClick={() => setShowEgyptianPrices(false)}
          >
            International Pricing
          </button>
          <button 
            className={`prdct_toggle-btn ${showEgyptianPrices ? 'prdct_active' : ''}`}
            onClick={() => setShowEgyptianPrices(true)}
          >
            Egyptian Pricing
          </button>
        </div>

        {showEgyptianPrices && (
          <div className="prdct_pricing-notice">
            <img 
              src={process.env.PUBLIC_URL + '/icons/landing/Egypt.png'} 
              alt="Egyptian Flag" 
              className="prdct_pricing-notice-icon"
            />
            <p>
              For Egyptian medical students and graduates, we provide <strong>financing support</strong> in the form of <strong>a more affordable payment plan</strong> based on the <strong>recurrent, aggressive currency devaluations</strong>.
              <br />
              <br />
              <span className="prdct_pricing-notice-disclaimer">
                * Prices are approximate <strong>according to USD exchange rate</strong> and may change periodically.
              </span>
            </p>
          </div>
        )}

        <section className="prdct_subscription-container">
            {/* Comparison Table */}
            <section className="prdct_compare-section">
              <h2 className="prdct_compare-title">Subscription Plans Comparison</h2>
              <div className="prdct_compare-table-wrapper">
                <div className="prdct_compare-description">
                  Compare our subscription plans to find the perfect fit for your USMLE preparation journey.
                </div>
                <table className="prdct_compare-table">
                  <thead>
                    <tr>
                      <th>Duration</th>
                      {showEgyptianPrices ? (
                        <th>
                          <div className="prdct_header-content">
                            <span className="prdct_header-icon">🇪🇬</span>
                            Egyptian Pricing
                          </div>
                        </th>
                      ) : (
                        <th>
                          <div className="prdct_header-content">
                            <span className="prdct_header-icon">🌍</span>
                            International Pricing
                          </div>
                        </th>
                      )}
                      <th>
                        <div className="prdct_header-content">
                          <span className="prdct_header-icon">⌛</span>
                          Exam Extension
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="prdct_highlight">
                      <td>
                        <div className="prdct_duration-cell">
                          <span className="prdct_duration-text">1 Month</span>
                          <span className="prdct_new-badge">New</span>
                        </div>
                      </td>
                      {showEgyptianPrices ? (
                        <td className="prdct_price-egyptian">1250 LE</td>
                      ) : (
                        <td className="prdct_price-international">$33</td>
                      )}
                      <td className="prdct_extension">No</td>
                    </tr>
                    <tr className="prdct_highlight">
                      <td>
                        <div className="prdct_duration-cell">
                          <span className="prdct_duration-text">3 Months</span>
                          <span className="prdct_new-badge">New</span>
                        </div>
                      </td>
                      {showEgyptianPrices ? (
                        <td className="prdct_price-egyptian">1500 LE</td>
                      ) : (
                        <td className="prdct_price-international">$45</td>
                      )}
                      <td className="prdct_extension">No</td>
                    </tr>
                    <tr>
                      <td>
                        <div className="prdct_duration-cell">
                          <span className="prdct_duration-text">6 Months</span>
                        </div>
                      </td>
                      {showEgyptianPrices ? (
                        <td className="prdct_price-egyptian">2000 LE</td>
                      ) : (
                        <td className="prdct_price-international">$60</td>
                      )}
                      <td className="prdct_extension">2 Months</td>
                    </tr>
                    <tr>
                      <td>
                        <div className="prdct_duration-cell">
                          <span className="prdct_duration-text">1 Year</span>
                        </div>
                      </td>
                      {showEgyptianPrices ? (
                        <td className="prdct_price-egyptian">3000 LE</td>
                      ) : (
                        <td className="prdct_price-international">$90</td>
                      )}
                      <td className="prdct_extension">4 Months</td>
                    </tr>
                    <tr className="prdct_highlight">
                      <td>
                        <div className="prdct_duration-cell">
                          <span className="prdct_duration-text">2 Years</span>
                          <span className="prdct_new-badge">New</span>
                        </div>
                      </td>
                      {showEgyptianPrices ? (
                        <td className="prdct_price-egyptian">4500 LE</td>
                      ) : (
                        <td className="prdct_price-international">$120</td>
                      )}
                      <td className="prdct_extension">6 Months</td>
                    </tr>
                    <tr className="prdct_highlight">
                      <td>
                        <div className="prdct_duration-cell">
                          <span className="prdct_duration-text">Lifetime</span>
                          <span className="prdct_new-badge">New</span>
                        </div>
                      </td>
                      {showEgyptianPrices ? (
                        <td className="prdct_price-egyptian">5000 LE</td>
                      ) : (
                        <td className="prdct_price-international">$160</td>
                      )}
                      <td className="prdct_extension">No Need</td>
                    </tr>
                    <tr className="prdct_best-value">
                      <td>
                        <div className="prdct_duration-cell">
                          <span className="prdct_duration-text">Lifetime Bundle (Both Libraries)</span>
                          <span className="prdct_best-value-badge">Best Value</span>
                        </div>
                      </td>
                      {showEgyptianPrices ? (
                        <td className="prdct_price-egyptian">7000 LE</td>
                      ) : (
                        <td className="prdct_price-international">$200</td>
                      )}
                      <td className="prdct_extension">No Need</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            {/* Library Subscriptions Row */}
            <div className="prdct_library-row">
              {subscriptionTypes
                .filter(sub => sub.type === 'library')
                .map((sub, index) => (
                  <div key={index} className="prdct_subscription-card">
                    <h2>{sub.title}</h2>
                    <p className="prdct_sub-description">{sub.description}</p>
                    
                    <div className="prdct_duration-options">
                      {sub.durations.map((duration, idx) => (
                        <div key={idx} className="prdct_duration-item">
                          <div className="prdct_duration-header">
                            <span className="prdct_duration">{renderDuration(duration.months)}</span>
                            <div className="prdct_price-container">
                              {duration.originalPrice && (
                                <span className="prdct_original-price">{duration.originalPrice}</span>
                              )}
                              <span className="prdct_price">{duration.price}</span>
                            </div>
                          </div>
                          <ul className="prdct_features-list">
                            {duration.features.map((feature, fidx) => (
                              <li key={fidx}>{feature}</li>
                            ))}
                          </ul>
                          {renderSubscriptionButtons(true, duration)}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
            </div>

            {/* Bundle Row */}
            <div className="prdct_bundle-row">
              {subscriptionTypes
                .filter(sub => sub.type === 'bundle')
                .map((sub, index) => (
                  <div key={index} className="prdct_subscription-card prdct_bundle-card">
                    <div className="prdct_bundle-badge">Best Value</div>
                    <div className="prdct_bundle-header">
                      <h2>{sub.title}</h2>
                      <p className="prdct_sub-description">{sub.description}</p>
                    </div>
                    
                    <div className="prdct_bundle-content">
                      <div className="prdct_bundle-price-section">
                        <div className="prdct_price-container">
                          <span className="prdct_price">{sub.price}</span>
                          <span className="prdct_price-label">One-time Payment</span>
                        </div>
                        {renderSubscriptionButtons(true, { months: -2 })}
                      </div>
                      <div className="prdct_bundle-features-section">
                        <ul className="prdct_features-list">
                          {sub.features.map((feature, fidx) => (
                            <li key={fidx}>{feature}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                ))}
            </div>

            {/* Mentorship Row */}
            <div className="prdct_mentorship-row">
              {subscriptionTypes
                .filter(sub => sub.type === 'mentorship')
                .map((sub, index) => (
                  <div key={index} className="prdct_subscription-card">
                    <h2>{sub.title}</h2>
                    <p className="prdct_sub-description">{sub.description}</p>
                    
                    <div className="prdct_service-pricing">
                      <div className="prdct_price-header">
                        <div className="prdct_price-container">
                          {sub.originalPrice && (
                            <span className="prdct_original-price">{sub.originalPrice}</span>
                          )}
                          <span className="prdct_price">{sub.price}</span>
                        </div>
                      </div>
                      <ul className="prdct_features-list">
                        {sub.features.map((feature, fidx) => (
                          <li key={fidx}>{feature}</li>
                        ))}
                      </ul>
                      {renderSubscriptionButtons(false, sub)}
                    </div>
                  </div>
                ))}
            </div>
        </section>

        {/* # Contact Information Aside */}
        <section className="prdct_contact-aside" ref={contactRef}>
          <div 
            className="prdct_contact-content"
            style={{
              background: `linear-gradient(rgba(44, 62, 80, 0.85), rgba(44, 62, 80, 0.85)), url(${process.env.PUBLIC_URL + '/icons/landing/sky1.jpg'})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat'
            }}
          >
            <div className="prdct_contact-header">
              <div className="prdct_contact-icon-wrapper">
                <img 
                  src="/icons/communicate.png" 
                  alt="Contact Icon" 
                  className="prdct_contact-icon"
                />
              </div>
              <h2>Ready to Begin Your USMLE Journey?</h2>
              <p className="prdct_contact-subheading">
                Connect with our team through any of these channels.
                <strong> Or click Subscribe Now, and we'll take you directly to WhatsApp to get started!</strong>
              </p>
              <button 
                className="prdct_subscribe-btn prdct_cta-button"
                onClick={() => handleSubscribe()}
              >
                Subscribe Now
              </button>
            </div>
            
            <div className="prdct_contact-list-wrapper">
              <div className="prdct_contact-grid">
                <a href="mailto:WeAreTEMPU@gmail.com" className="prdct_contact-link email">
                  <MdEmail className="prdct_contact-icon" />
                  <span>WeAreTEMPU@gmail.com</span>
                </a>
                <a 
                  href="https://wa.me/201225040602" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="prdct_contact-link whatsapp"
                >
                  <FaWhatsapp className="prdct_contact-icon" />
                  <span>WhatsApp</span>
                </a>
                <a 
                  href="https://x.com/We_Are_TEMPU" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="prdct_contact-link twitter"
                >
                  <FaXTwitter className="prdct_contact-icon" />
                  <span>Twitter/X</span>
                </a>
                <a 
                  href="https://www.facebook.com/profile.php?id=61563424020612" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="prdct_contact-link facebook"
                >
                  <FaFacebook className="prdct_contact-icon" />
                  <span>Facebook</span>
                </a>
                <a 
                  href="https://www.instagram.com/we_are_tempu" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="prdct_contact-link instagram"
                >
                  <FaInstagram className="prdct_contact-icon" />
                  <span>Instagram</span>
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <DashboardFooter isDarkMode={isDarkMode} />
    </div>
  );
}

export default Products; 